import {
    addContainerClassesNoRules,
    multiClasses,
} from '@dat/iq-style-processor';
import {
    Component as ComponentType,
    ComponentTypeValues,
    Section,
} from '../../models';
import { idCreator } from '../../models/builder-utils';
import {
    Controls,
    EquipTypes,
    createSectionClasses,
    createdHeader,
    createdHeaderComponent,
} from './shared.models';
import { ViewTitleSection } from './view-trends-dashboard.models';

const MapComponentTypes: Record<EquipTypes, ComponentTypeValues> = {
    VAN: 'TrendlinesLtMapVan',
    REEFER: 'TrendlinesLtMapReefer',
    FLATBED: 'TrendlinesLtMapFlatbed',
};
const GraphComponentTypes: Record<EquipTypes, ComponentTypeValues> = {
    VAN: 'TrendlinesLtGraphVan',
    REEFER: 'TrendlinesLtGraphReefer',
    FLATBED: 'TrendlinesLtGraphFlatbed',
};
const NationalGraphComponentTypes: Record<EquipTypes, ComponentTypeValues> = {
    VAN: 'TrendlinesLtNationalGraphVan',
    REEFER: 'TrendlinesLtNationalGraphReefer',
    FLATBED: 'TrendlinesLtNationalGraphFlatbed',
};

const DemandAndCapacityTitles: Record<EquipTypes, string> = {
    VAN: 'Van Demand & Capacity',
    REEFER: 'Reefer Demand & Capacity',
    FLATBED: 'Flatbed Demand & Capacity',
};

const LtrTitle: Record<EquipTypes, string> = {
    VAN: 'Van Load-to-Truck Ratio',
    REEFER: 'Reefer Load-to-Truck Ratio',
    FLATBED: 'Flatbed Load-to-Truck Ratio',
};

// #region L/T Map
const LtMapComponent = (idRoot: string, type: EquipTypes): ComponentType => ({
    id: idCreator(idRoot, ['lt-map']),
    type: MapComponentTypes[type],
    props: {
        id: idCreator(idRoot, ['lt-map']),
        type,
    },
    classNames: {
        ...multiClasses(
            ['w-full', 'mt-10'],
            ['[data-uitype="builder-component"]'],
        ),
        ...multiClasses(
            ['!h-[500px]', 'sm:!h-[300px]'],
            ['[data-uitype="graph"]'],
        ),
    },
});
// #endregion L/T Map

// #region L/T Graph
const LtGraphComponent = (idRoot: string, type: EquipTypes): ComponentType => ({
    id: idCreator(idRoot, ['lt-graph']),
    type: GraphComponentTypes[type],
    props: {
        id: idCreator(idRoot, ['lt-graph']),
        type,
    },
    classNames: {
        ...multiClasses(
            ['w-full', 'mb-10', 'w-[84%]', 'm-auto'],
            ['[data-uitype="builder-component"]'],
        ),
        ...multiClasses(
            ['!bg-black', '!h-[500px]', 'sm:!h-[300px]'],
            ['[data-uitype="graph"]'],
        ),
        ...multiClasses(['bg-black'], ['[data-uitype="graph-legend"]']),
    },
});
// #endregion L/T Graph

// #region L/T Graph
const LtNationalGraphComponent = (
    idRoot: string,
    type: EquipTypes,
): ComponentType => ({
    id: idCreator(idRoot, ['lt-graph']),
    type: NationalGraphComponentTypes[type],
    props: {
        id: idCreator(idRoot, ['lt-graph']),
        type,
    },
    classNames: {
        ...multiClasses(
            ['w-[300px]', 'mt-10'],
            ['[data-uitype="builder-component"]'],
        ),

        ...addContainerClassesNoRules([
            'col-span-1',
            'px-[10%]',
            'relative',
            'h-[200px]',
            'w-[270px]',
            'm-auto',
            'tablet:mt-10',
        ]),
    },
});
// #endregion L/T Graph
export const DemandSharedSections = (
    idRoot: string,
    type: EquipTypes,
): Section[] => {
    return [
        {
            ...ViewTitleSection,
            classNames: {
                ...addContainerClassesNoRules(['bg-transparent', 'col-span-2']),
                ...multiClasses(
                    ['!bg-transparent', 'max-w-[1140px]', 'mx-auto'],
                    ['[data-uitype="builder-section"]'],
                ),
            },
        },
        {
            id: idCreator(idRoot, ['section', 'map']),
            components: [
                createdHeaderComponent(
                    idRoot,
                    type,
                    DemandAndCapacityTitles[type],
                ),
                Controls(
                    idCreator(idRoot, ['section', 'map', 'controls']),
                    `/trendlines/${type.toLowerCase()}/demand-and-capacity`,
                ),
                LtMapComponent(
                    idCreator(idRoot, ['section', 'map', 'viz']),
                    type,
                ),
                {
                    id: idCreator(
                        idCreator(idRoot, ['section', 'rates-graph']),
                        ['graph-title'],
                    ),
                    classNames: {
                        ...multiClasses(
                            [
                                'w-full',
                                'text-white',
                                'font-caption-micro',
                                'italic',
                                'my-4',
                                'sm:px-[25px]',
                                'tablet:px-[25px]',
                            ],
                            ['[data-uitype="builder-component"]'],
                        ),
                        ...multiClasses(
                            [
                                'no-underline',
                                'hover:underline',
                                'text-dat-blue-500',
                            ],
                            ['a'],
                        ),
                        ...multiClasses(['tl-graph-title'], ['label']),
                    },
                    type: 'Label',
                    props: {
                        id: idCreator(
                            idCreator(
                                idCreator(idRoot, ['section', 'rates-graph']),
                                ['graph-title'],
                            ),
                            ['title'],
                        ),
                        label: `Market Conditions maps in <a href="https://www.dat.com/freight-rates/rateview" target="_parent">DAT RateView</a> depict geographic trends in the load-to-truck ratio.`,
                    },
                },
                {
                    id: idCreator(idRoot, ['section', 'map', 'label']),
                    type: 'Label',
                    props: {
                        id: idCreator(idRoot, ['section', 'map', 'label']),
                        label: `Check the <a href="https://www.dat.com/blog" target="_parent">**DAT Freight Talk blog**</a> for regular updates on market conditions,
                        regulations and industry trends.`,
                    },
                    classNames: {
                        ...multiClasses(
                            ['w-full', 'mt-10', 'text-white', 'sm:px-[25px]','tablet:px-[25px]'],
                            ['[data-uitype="builder-component"]'],
                        ),
                        ...multiClasses(
                            [
                                'no-underline',
                                'hover:underline',
                                'text-dat-blue-500',
                            ],
                            ['a'],
                        ),
                        ...multiClasses(['tl-map-title'], ['label']),
                    },
                },
            ],
            classNames: { ...createSectionClasses(2, false) },
        },
        {
                    id: idCreator(idRoot, ['section', 'mc-upsell']),
                    components: [
                        {
                            id: idCreator(idRoot, [
                                'section',
                                'mc-upsell',
                                'banner',
                            ]),
                            type: 'TrendlinesUpsellBanner',
                            props: {
                                id: idCreator(idRoot, [
                                    'section',
                                    'mc-upsell',
                                    'banner',
                                ]),
                                type: 'MarketConditions',
                                equipmentType: type,
                            },
                            cssClassRules: [
                                {
                                    classNames: ['text-black', 'w-full'],
                                    cssSelectors: [''],
                                },
                            ],
                        },
                    ],
                    classNames: {
                        ...createSectionClasses(2, true),
                        ...multiClasses(
                            ['!pb-[100px]', 'sm:pb-[50px]','!pt-0'],
                            ['[data-uitype="builder-section"]'],
                        ),
                    },
                },
        {
            id: idCreator(idRoot, ['section', 'lt-graph']),
            components: [
                createdHeaderComponent(idRoot, type, LtrTitle[type]),
                LtGraphComponent(
                    idCreator(idRoot, ['section', 'lt-graph', 'viz']),
                    type,
                ),
                {
                    id: idCreator(
                        idCreator(idRoot, ['section', 'rates-graph']),
                        ['graph-title'],
                    ),
                    classNames: {
                        ...multiClasses(
                            [
                                'w-full',
                                'text-white',
                                'font-caption-micro',
                                'italic',
                                'sm:px-[25px]',
                                'tablet:px-[25px]',
                            ],
                            ['[data-uitype="builder-component"]'],
                        ),
                        ...multiClasses(
                            [
                                'no-underline',
                                'hover:underline',
                                'text-dat-blue-500',
                            ],
                            ['a'],
                        ),
                        ...multiClasses(['tl-graph-title'], ['label']),
                    },
                    type: 'Label',
                    props: {
                        id: idCreator(
                            idCreator(
                                idCreator(idRoot, ['section', 'rates-graph']),
                                ['graph-title'],
                            ),
                            ['title'],
                        ),
                        label: `The chart above depicts the load-to-truck ratio in the past 13 months, derived from <a href="https://www.dat.com/freight-rates" target="_parent">DAT RateView</a>.`,
                    },
                },
            ],
            classNames: {
                ...createSectionClasses(2, true),
            },
        },
        {
            id: idCreator(idRoot, ['section', 'lt-ratio']),
            title: '',
            components: [
                {
                    id: idCreator(idCreator(idRoot, ['section', 'lt-graph']), [
                        'about-title',
                    ]),
                    type: 'Label',
                    classNames: {
                        ...addContainerClassesNoRules([
                            'col-span-1',
                            'px-[16%]',
                            'sm:px-0',
                            'sm:row-start-1',
                            'tablet:row-start-1',
                            'tablet:px-[25px]',
                        ]),
                        ...multiClasses(['sm:mb-0'], ['h2']),
                    },
                    props: {
                        id: idCreator(
                            idCreator(
                                idCreator(idRoot, ['section', 'lt-graph']),
                                ['about-title'],
                            ),
                            ['title'],
                        ),
                        label: createdHeader(
                            `About The National Load-To-Truck Ratio`,
                        ),
                    },
                },
                {
                    id: idCreator(idCreator(idRoot, ['section', 'lt-graph']), [
                        'graph-title',
                    ]),
                    classNames: {
                        ...addContainerClassesNoRules([
                            'col-span-1',
                            'px-[16%]',
                            'sm:px-0',
                            'tablet:px-[25px]',
                            'tablet:pb-[25px]',
                            'tablet:!mb-[25px]'
                        ]),
                    },
                    type: 'Label',
                    props: {
                        id: idCreator(
                            idCreator(
                                idCreator(idRoot, ['section', 'lt-graph']),
                                ['graph-title'],
                            ),
                            ['title'],
                        ),
                        label: createdHeader(`National Load-To-Truck Ratio`),
                    },
                },
                {
                    id: idCreator(idCreator(idRoot, ['section', 'lt-graph']), [
                        'about-description',
                    ]),
                    classNames: {
                        ...addContainerClassesNoRules([
                            'col-span-1',
                            'px-[16%]',
                            'sm:px-[25px]',
                            'tablet:px-10',
                            'sm:row-start-2',
                            'tablet:row-start-2',
                            'sm:mb-10',
                        ]),
                        ...multiClasses(
                            [
                                'no-underline',
                                'hover:underline',
                                'text-dat-blue-500',
                            ],
                            ['a'],
                        ),
                        ...multiClasses(['tl-description'], ['label']),
                    },
                    type: 'Label',
                    props: {
                        id: idCreator(
                            idCreator(
                                idCreator(idRoot, ['section', 'lt-graph']),
                                ['about-description'],
                            ),
                            ['description'],
                        ),
                        label: `Load-to-truck ratios represent the number of loads
                                posted for every truck posted on <a href="https://www.dat.com/loadboards" target="_parent">**DAT Load Board**</a>. The load-to-truck ratio is a sensitive,
                                real-time indicator of the balance between spot market demand and capacity.
                                Changes in the ratio often signal impending changes in rates.
                                <a href="https://www.dat.com/blog/post/what-is-the-load-to-truck-ratio" target="_parent">**Learn more about load-to-truck ratios here.</a>**`,
                    },
                },
                LtNationalGraphComponent(
                    idCreator(idRoot, ['section', 'lt-national-graph', 'viz']),
                    type,
                ),
            ],
            classNames: {
                ...createSectionClasses(2, false),
                ...multiClasses(
                    [
                        'grid',
                        'grid-cols-2',
                        'sm:grid-cols-1',
                        'tablet:grid-cols-1',
                        'sm:gap-x-0',
                        'tablet:gap-x-0',
                        'sm:items-stretch',
                        'tablet:items-stretch',
                        'sm:justify-stretch',
                        'tablet:justify-stretch',
                        'gap-x-2',
                        'gap-y-10',
                        'mb-[50px]',
                        'mt-[50px]',
                    ],
                    ['[data-uitype="builder-section"]'],
                ),
            },
        },
    ];
};
